/**
 * `parseCommand` — the pipeline orchestrator (plan §2/§3): fold → split →
 * per clause: tokenize → canonicalize → structure → resolve → rank → assemble.
 *
 * @remarks
 * Pure and synchronous end to end: same input + same context ⇒ same outcome,
 * byte for byte. Clauses parse independently, left to right, and the first
 * clarification or failure stops the walk with the cleanly-parsed prefix
 * attached (plan §2.1) — the parser cannot know whether clause 1 will succeed
 * in the world, so the consumer decides what a prefix is worth.
 *
 * M4 landed the full single-clause path and the natural multi-clause walk;
 * M5 slots the fuzzy pass between canonicalization and structure matching —
 * corrections thread into confidence (§6.1), onto `ParsedCommand.corrections`,
 * and into `nearMissSuggestions`. M6 threads in-input recency through the
 * walk: each parsed clause's resolved refs feed the next clause's pronoun
 * view (plan §4).
 *
 * D18 (2026-08-13) — parse-guided near-tie resolution. A near-tie is no
 * longer an immediate dead end: each tied target is tried through the REAL
 * downstream pipeline (substitute → re-correct → structure → resolve → rank),
 * and a reading that UNIQUELY parses wins, carrying the substitution as an
 * ordinary correction ("open gaet" reads as `open gate` because `get` cannot
 * parse there; "gaet key" reads as `get key` because `gate` cannot). Grammar
 * is the judge — no positional heuristics, no length preferences. GENUINE
 * ambiguity (several tied readings parse, or none do) falls back to the §6.2
 * policy exactly as before: a clarification when every tied target is a
 * catalog word, an `unrecognized` with suggestions otherwise. Nested ties
 * resolve the same way down to {@link NEAR_TIE_RESOLUTION_DEPTH_CAP}, beyond
 * which the §6.2 fallback applies — deterministic and adversarial-input-proof.
 *
 * D21 (2026-08-13) — parse-guided verb demotion. Mid-clause intent tokens are
 * no longer automatic `second_verb` dead ends: the offender demotes to its
 * surface word and the clause re-walks the pipeline, winning only when the
 * downstream stages vouch for the demoted reading (a command or a
 * clarification). Vocabularies may now alias verbs that also appear inside
 * entity phrases (`pry` the intent vs "take the pry bar") without breaking
 * either reading — see {@link demoteMidClauseVerbsAndReparse}.
 *
 * Deterministic guards: at most {@link CLAUSE_COUNT_CAP} clauses per input
 * (beyond → the whole input is rejected, plan §5); clause outcomes whose
 * composed confidence falls below the caller's floor (default
 * {@link DEFAULT_CONFIDENCE_FLOOR}) become `unrecognized` (plan §6.1).
 *
 * @packageDocumentation
 */

// cspell:ignore gaet sdasd zzqqxxvv -- deliberate player typos and mash: D18's motivating case, D28's keyboard-mash example, D80's merged-clause repro

import { askAboutHeadVerbCorrection } from './fuzzy/ask-about-head-verb.js';
import { buildFuzzyCandidatePool, correctClauseTokens, type FuzzyCandidatePool } from './fuzzy/correct-clause-tokens.js';
import { DEFAULT_FUZZY_THRESHOLDS } from './fuzzy/correct-token.js';
import { canonicalizeTokens } from './grammar/canonicalize-tokens.js';
import { type ClauseStructure, matchClauseStructure, type PhraseToken } from './grammar/match-clause-structure.js';
import { markPrepositionsInsideNames } from './grammar/prepositions-inside-names.js';
import { isQuestionFormIntent } from './grammar/question-form.js';
import { readOrder } from './grammar/read-order.js';
import { SOFT_FILLER_WORDS } from './lexicon/builtin-function-words.js';
import {
	buildParsedCommand,
	buildStructureDiagnostics,
	type ClausePhraseResolutions,
	restoreOriginalSurfaces,
} from './outcome/build-parse-outcome.js';
import { type ParseContext } from './parse-context.js';
import { scoreParseCandidate } from './rank/score-parse-candidate.js';
import {
	catalogHeadWordsOf,
	type ClauseParseResult,
	type ClauseSegment,
	demoteMidClauseVerbsAndReparse,
	nearTieResult,
	resolveNearTieByParsing,
} from './rescue/parse-guided-rescues.js';
import { recencyViewForClause } from './resolve/pronoun-antecedents.js';
import { readPhraseAsPlural } from './resolve/read-plural-phrase.js';
import { type ResolvedEntityPhrase } from './resolve/resolve-entity-phrase.js';
import { readAmongContents, resolveRolePhrase } from './resolve/resolve-role-phrase.js';
import { applyShapeLadder, readClauseAsNounPhrase, type ShapeLadderInput, withLanguageEvidence } from './shape/apply-shape-ladder.js';
import { adoptableResplit, splitInputIntoClauseSegments } from './tokenize/clause-segmentation.js';
import {
	type ClarificationRequest,
	type EntityRef,
	type ParsedCommand,
	type ParseDiagnostics,
	type ParseOutcome,
	type TokenCorrection,
} from './types.js';

/** The most clauses one input may chain (plan §5) — beyond it, the input is rejected whole. */
export const CLAUSE_COUNT_CAP = 5;

/**
 * How many near-ties one clause walk may resolve by parsing (D18) before
 * falling back to the §6.2 policy. Two covers every plausible human input
 * (a typo'd verb AND a typo'd noun in one clause); the cap exists so an
 * adversarial clause of mutually-tying tokens cannot branch unboundedly.
 */
export const NEAR_TIE_RESOLUTION_DEPTH_CAP = 2;

export { type ParseContext } from './parse-context.js';

/** The default confidence floor (plan §6.1): clause outcomes below it become `unrecognized`. */
export const DEFAULT_CONFIDENCE_FLOOR = 0.3;

/**
 * Parse one raw player input against a compiled vocabulary and entity catalog.
 *
 * @param rawInput - Exactly what the player typed
 * @param context - The vocabulary, catalog, options, and recency view
 * @returns Command proposals, a clarification, or diagnostics (plan §2.1)
 */
export const parseCommand = (rawInput: string, context: ParseContext): ParseOutcome => {
	const clauseSegments = splitInputIntoClauseSegments(rawInput, context.lexicon);

	if (clauseSegments.length === 0) {
		return unrecognized(rawInput, [], { failureKind: 'blank_input', unmatchedTokens: []});
	}

	if (clauseSegments.length > CLAUSE_COUNT_CAP) {
		return unrecognized(rawInput, [], {
			failureKind: 'structure_not_understood',
			unmatchedTokens: clauseSegments.slice(CLAUSE_COUNT_CAP).map((clauseSegment) => clauseSegment.clauseText),
		});
	}

	// One correction pool per parse call: the pool depends only on vocabulary +
	// catalog (+ the consumer's known words, D211), all fixed for the whole input.
	const fuzzyPool = buildFuzzyCandidatePool(context.lexicon, context.entities, context.knownWords);
	const contextRecencyRefs = context.recentEntityRefs ?? [];
	const parsedCommands: ParsedCommand[] = [];

	// In-input recency threading (plan §4, M6): each parsed clause prepends its
	// resolved refs — direct object first, then the bound phrase's — to the view
	// the NEXT clause resolves pronouns against, ahead of the caller-supplied
	// list. `take the keycard then swipe it` — "it" is the keycard. Purely
	// parser-internal; the caller's list is never mutated.
	let threadedRecencyRefs: readonly EntityRef[] = [];
	// A WORKING list rather than a plain iteration, because a failing merged
	// clause may be re-split mid-walk (D80) — adopted subsegments rejoin the
	// walk in place of the segment they came from.
	let remainingSegments: readonly ClauseSegment[] = clauseSegments;

	for (;;) {
		const [clauseSegment, ...restSegments] = remainingSegments;

		// Segments exhausted: every clause parsed (plan §2.1's happy path).
		if (clauseSegment === undefined) {
			return { commands: parsedCommands, originalInput: rawInput, type: 'parsed' };
		}

		const recencyView = [...threadedRecencyRefs, ...contextRecencyRefs];
		// D252: an order to somebody is read before the clause is read as the player's own act — see read-order.ts.
		const order = readOrder(clauseSegment, restSegments[0], context, recencyView, (orderedSegment) =>
			parseOneClause(orderedSegment, context, fuzzyPool, recencyView),
		);
		const walked = order?.result ?? parseOneClause(clauseSegment, context, fuzzyPool, recencyView);
		// D212: a reading whose VERB the fuzzy rung supplied is a question for
		// the consumer, never an act — judged on the finished clause, so a D18
		// tie walk settles the tie first (see ask-about-head-verb.ts).
		const clauseResult = askAboutHeadVerbCorrection(clauseSegment.clauseText, walked, context.lexicon);

		if (clauseResult.type === 'command') {
			parsedCommands.push(clauseResult.command);
			threadedRecencyRefs = [...resolvedRefsOf(clauseResult.command), ...threadedRecencyRefs];
			remainingSegments = remainingSegments.slice(order?.segmentsRead ?? 1);
			continue;
		}

		/*
		 * D80 (Calder's V5): a failing clause that swallowed a conjunction may be
		 * a failed clause BOUNDARY, not a failed phrase — `inspect the lamp and
		 * zzqqxxvv` merged because "zzqqxxvv" leads no clause, then failed whole,
		 * taking the perfectly good lamp down with it. Before the failure
		 * surfaces, re-read the segment's interior conjunctions as boundaries and
		 * adopt that reading ONLY when its first piece stands on its own (parses,
		 * or asks a real tie). `take the black and white keycard` stays merged:
		 * "take the black" vouches for nothing.
		 */
		const resplitSegments = adoptableResplit(
			clauseSegment,
			CLAUSE_COUNT_CAP - parsedCommands.length - restSegments.length,
			(subsegment) =>
				parseOneClause(subsegment, context, fuzzyPool, [...threadedRecencyRefs, ...contextRecencyRefs]).type !==
				'diagnostics',
		);

		if (resplitSegments !== undefined) {
			remainingSegments = [...resplitSegments, ...restSegments];
			continue;
		}

		// §8.8: whichever way this clause stops the walk, the clauses BEYOND it
		// were never parsed — name them, so the consumer can say so out loud.
		const remainingClauseTexts = restSegments.map((remaining) => remaining.clauseText);
		const remainder = remainingClauseTexts.length === 0 ? {} : { remainingClauseTexts };

		if (clauseResult.type === 'clarification') {
			return {
				clarification: clauseResult.clarification,
				originalInput: rawInput,
				parsedPrefix: parsedCommands,
				...remainder,
				type: 'needs_clarification',
			};
		}

		return {
			...unrecognized(rawInput, parsedCommands, {
				...withLanguageEvidence(clauseResult.diagnostics, clauseSegment.tokens, context.lexicon, fuzzyPool.legalWordSet),
				// The one place a clause failure becomes an outcome, so the one place
				// the failing clause is named (its folded text, like a clarification's
				// `clauseText`) — the F5 law: questions park against the CLAUSE.
				failedClauseText: clauseSegment.clauseText,
			}),
			...remainder,
		};
	}
};

/** How resolving one clause's phrases ended (internal). */
type ClauseResolutionResult =
	| { readonly clarification: ClarificationRequest; readonly type: 'clarification' }
	| { readonly diagnostics: ParseDiagnostics; readonly type: 'diagnostics' }
	| { readonly resolutions: ClauseResolutions; readonly type: 'resolved' };

/** The resolved phrases of one clause, with the fields command assembly and scoring read. */
interface ClauseResolutions extends ClausePhraseResolutions {
	readonly entityAliasMatchCount: number;
	readonly pronounResolutionCount: number;
}

/**
 * Run one clause through canonicalize → fuzzy → structure → resolve → rank →
 * assemble.
 *
 * @param clauseSegment - The clause to parse (tokens may carry D18 substitutions already patched in)
 * @param context - The parse context
 * @param fuzzyPool - The parse call's shared correction pool
 * @param recencyView - Most-recent-first refs for this clause's pronouns (threaded + caller-supplied)
 * @param seedCorrections - Corrections already applied to `clauseSegment.tokens` by D18 tie resolution — prepended to this walk's own so scoring, assembly, and UI echoes treat them identically
 * @param tieResolutionDepth - How many further near-ties this walk may resolve by parsing (D18); at 0 the §6.2 fallback applies
 * @returns The clause's command, clarification, or diagnostics
 */
const parseOneClause = (
	clauseSegment: ClauseSegment,
	context: ParseContext,
	fuzzyPool: FuzzyCandidatePool,
	recencyView: readonly EntityRef[],
	seedCorrections: readonly TokenCorrection[] = [],
	tieResolutionDepth: number = NEAR_TIE_RESOLUTION_DEPTH_CAP,
): ClauseParseResult => {
	// D26: conversational filler ("what is the weather LIKE", "open it RIGHT
	// NOW") yields to vocabulary — a soft-filler word is stripped ONLY when
	// nothing in this parse's legal-word set (compiled vocabulary + catalog)
	// claims it. Never strip the whole clause: an all-filler input fails
	// honestly, quoting itself.
	const strippedTokens = clauseSegment.tokens.filter(
		(clauseToken) => !SOFT_FILLER_WORDS.has(clauseToken.text) || fuzzyPool.legalWordSet.has(clauseToken.text),
	);
	const clauseTokens = strippedTokens.length === 0 ? clauseSegment.tokens : strippedTokens;
	const firstPassTokens = canonicalizeTokens(clauseTokens, context.lexicon);
	const correctionOutcome = correctClauseTokens(
		clauseTokens,
		firstPassTokens,
		context.lexicon,
		fuzzyPool,
		context.options?.fuzzyThresholds ?? DEFAULT_FUZZY_THRESHOLDS,
	);

	if (correctionOutcome.type === 'near_tie') {
		if (tieResolutionDepth === 0) {
			return nearTieResult(correctionOutcome.tie, fuzzyPool, clauseSegment.clauseText);
		}

		// D18 candidate walks recurse through THIS function with one less depth.
		return resolveNearTieByParsing(
			correctionOutcome.tie,
			clauseSegment,
			fuzzyPool,
			(candidateSegment, candidateSeedCorrections, candidateDepth) =>
				parseOneClause(candidateSegment, context, fuzzyPool, recencyView, candidateSeedCorrections, candidateDepth),
			seedCorrections,
			tieResolutionDepth,
		);
	}

	const corrections = [...seedCorrections, ...correctionOutcome.corrections];
	// D242: a declared preposition inside one of the catalog's own names is marked as the name's.
	const canonicalTokens = markPrepositionsInsideNames(correctionOutcome.tokens, context.lexicon, context.entities);

	// D235: the caller's antecedent for this clause's pronoun word leads the view, when it names one.
	const clauseRecencyView = recencyViewForClause(canonicalTokens, recencyView, context.pronounAntecedents);
	const ladderInput: ShapeLadderInput = {
		canonicalTokens,
		clauseText: clauseSegment.clauseText,
		clauseTokens,
		corrections,
		entities: context.entities,
		lexicon: context.lexicon,
		recentEntityRefs: clauseRecencyView,
		reparseWithoutCourtesyPrefix: (courtesyStrippedTokens) =>
			parseOneClause(
				{ clauseText: clauseSegment.clauseText, tokens: courtesyStrippedTokens },
				context,
				fuzzyPool,
				recencyView,
				[],
				tieResolutionDepth,
			),
	};

	// D133: an answer slot reads the clause as a name FIRST — the question
	// already supplied the verb, so a name that happens to lead with one
	// ("pry bar", D64) must not parse as that verb.
	if (context.options?.readAsNounPhrase === true) {
		const nounPhraseResult = readClauseAsNounPhrase(ladderInput);

		if (nounPhraseResult !== undefined) {
			return nounPhraseResult;
		}
	}

	const structureOutcome = matchClauseStructure(canonicalTokens, context.lexicon);

	if (structureOutcome.type === 'unmatched') {
		// D21: a second-verb failure earns a parse-guided rescue attempt before
		// it becomes a diagnostic — see demoteMidClauseVerbsAndReparse.
		const demotedResult = demoteMidClauseVerbsAndReparse(
			structureOutcome.diagnostic,
			canonicalTokens,
			context.lexicon,
			(demotedStructure) =>
				completeClauseWalk(demotedStructure, clauseSegment.clauseText, context, clauseRecencyView, corrections),
			catalogHeadWordsOf(context.entities),
		);

		if (demotedResult !== undefined) {
			return demotedResult;
		}

		// D29: the imperative grammar is beaten — offer the clause to the
		// sentence-shape ladder before shipping a raw structure diagnostic.
		const shapeResult = applyShapeLadder(ladderInput);

		if (shapeResult !== undefined) {
			return shapeResult;
		}

		return {
			diagnostics: buildStructureDiagnostics(structureOutcome.diagnostic, canonicalTokens, corrections),
			type: 'diagnostics',
		};
	}

	return completeClauseWalk(structureOutcome.structure, clauseSegment.clauseText, context, clauseRecencyView, corrections);
};

/**
 * The shared tail of one clause walk: resolve → rank → assemble, with the
 * confidence floor applied (plan §6.1). Both the straight path and the D21
 * demotion retry end here, so scoring and floor policy cannot drift between
 * them.
 *
 * @param structure - The matched grammatical reading
 * @param clauseText - The clause, folded
 * @param context - The parse context
 * @param recencyView - Most-recent-first refs for this clause's pronouns
 * @param corrections - Every correction applied to this clause so far
 * @returns The clause's command, clarification, or diagnostics
 */
const completeClauseWalk = (
	structure: ClauseStructure,
	clauseText: string,
	context: ParseContext,
	recencyView: readonly EntityRef[],
	corrections: readonly TokenCorrection[],
): ClauseParseResult => {
	const resolutionResult = resolveClausePhrases(structure, clauseText, context, recencyView, corrections);

	if (resolutionResult.type !== 'resolved') {
		return resolutionResult;
	}

	const { resolutions } = resolutionResult;
	const confidence = scoreParseCandidate({
		corrections,
		entityAliasMatchCount: resolutions.entityAliasMatchCount,
		pronounResolutionCount: resolutions.pronounResolutionCount,
		wasIntentAliasMatched: wasIntentAliasMatched(structure),
	});
	const confidenceFloor = context.options?.confidenceFloor ?? DEFAULT_CONFIDENCE_FLOOR;

	if (confidence < confidenceFloor) {
		// Below the floor, committing to a reading would be a guess (plan §6.1).
		// `structure_not_understood` is the closest documented kind; the applied
		// corrections ride along as near-miss suggestions so the consumer can
		// still teach ("did you mean ...?").
		const nearMissSuggestions = [...new Set(corrections.map((correction) => correction.correctedText))];

		return {
			diagnostics: {
				failureKind: 'structure_not_understood',
				unmatchedTokens: [],
				...(nearMissSuggestions.length > 0 ? { nearMissSuggestions } : {}),
			},
			type: 'diagnostics',
		};
	}

	return {
		command: buildParsedCommand(clauseText, structure, resolutions, confidence, corrections),
		type: 'command',
	};
};

/**
 * Resolve the phrases a structure carved out — direct object first, then the
 * bound phrase (the player's reading order decides which failure or
 * clarification surfaces first).
 *
 * @param structure - The matched structure
 * @param clauseText - The clause, folded (clarifications cite it)
 * @param context - The parse context
 * @param recencyView - Most-recent-first refs for pronoun heads (fixed as of this clause's start — no intra-clause threading)
 * @param corrections - The clause's applied corrections (D26: failures quote ORIGINAL surfaces)
 * @returns The resolutions plus scoring evidence, or the first clarification/failure
 */
const resolveClausePhrases = (
	structure: ClauseStructure,
	clauseText: string,
	context: ParseContext,
	recencyView: readonly EntityRef[],
	corrections: readonly TokenCorrection[],
): ClauseResolutionResult => {
	const resolvedPhrases: {-readonly [role in keyof ClausePhraseResolutions]?: ResolvedEntityPhrase } = {};
	// D227: the thing named after a bearing (`go up THE ROAD`) must be a thing, or the clause fails as `take the road` would.
	const phrasesToResolve: readonly { readonly phrase: readonly PhraseToken[]; readonly role: keyof ClausePhraseResolutions }[] = [
		...('objectPhrase' in structure ? [{ phrase: structure.objectPhrase, role: 'objectResolution' as const }] : []),
		...('boundPhrase' in structure ? [{ phrase: structure.boundPhrase, role: 'boundResolution' as const }] : []),
		...('destinationPhrase' in structure && structure.destinationPhrase !== undefined
			? [{ phrase: structure.destinationPhrase, role: 'destinationResolution' as const }]
			: []),
	];

	// D134: the verb's side of the room applies to its DIRECT object; a bound
	// phrase (a tool, a destination) declares no preference and asks on a tie.
	const objectPreference = 'intentToken' in structure ? structure.intentToken.intent.objectPreference : 'none';

	// D261: a bound phrase that says where the object is settles an object that ties.
	const locatingPhrase =
		'preposition' in structure && structure.preposition.word !== undefined && structure.intentToken.intent.objectLocatingPrepositions.has(structure.preposition.word)
			? structure.boundPhrase
			: undefined;

	for (const { phrase, role } of phrasesToResolve) {
		const phraseOutcome =
			role === 'objectResolution'
				? readAmongContents(resolveRolePhrase({ objectPreference, phrase }, context, recencyView), { objectPreference, phrase }, locatingPhrase, context, recencyView)
				: resolveRolePhrase({ objectPreference: 'none', objectRef: resolvedPhrases.objectResolution?.entityRef, phrase }, context, recencyView);

		// D217: a phrase that placed nobody as written may be a PLURAL — the
		// second reading, never the first (see read-plural-phrase.ts).
		const pluralReading =
			phraseOutcome.type === 'not_found' ? readPhraseAsPlural(phrase, context.entities, role === 'boundResolution', clauseText) : undefined;

		if (pluralReading?.type === 'clarification') {
			return pluralReading;
		}

		if (pluralReading !== undefined) {
			resolvedPhrases[role] = pluralReading.resolution;
			continue;
		}

		if (phraseOutcome.type === 'not_found') {
			return {
				diagnostics: {
					failureKind: 'entity_not_found',
					unmatchedTokens: restoreOriginalSurfaces(phraseOutcome.unmatchedPhraseTokens, corrections),
					// D48: report-only. A question-form alias (`what is`) makes a
					// question PARSE, so its unresolvable subject arrives here rather
					// than at the shape ladder. Saying so costs nothing and is the
					// only place that knows.
					...(isQuestionFormIntent(structure, context.lexicon) ? { wasQuestionFormIntent: true } : {}),
				},
				type: 'diagnostics',
			};
		}

		if (phraseOutcome.type === 'needs_clarification') {
			return {
				clarification: { candidates: phraseOutcome.candidates, clauseText, kind: 'which_entity' },
				type: 'clarification',
			};
		}

		resolvedPhrases[role] = phraseOutcome.resolution;
	}

	const resolutionList = [resolvedPhrases.objectResolution, resolvedPhrases.boundResolution, resolvedPhrases.destinationResolution];

	return {
		resolutions: {
			...resolvedPhrases,
			entityAliasMatchCount: resolutionList.filter((resolution) => resolution?.wasAliasMatched === true).length,
			pronounResolutionCount: resolutionList.filter((resolution) => resolution?.pronounResolution !== undefined).length,
		},
		type: 'resolved',
	};
};

/**
 * Did this structure's intent surface through an alias? (Bare directions bind
 * a synthesized/authored movement intent — never alias-matched. Direction
 * aliases themselves carry no confidence weight; see `rank/score-parse-candidate`.)
 *
 * @param structure - The matched structure
 * @returns Whether ×0.97 applies for the intent (plan §6.1)
 */
const wasIntentAliasMatched = (structure: ClauseStructure): boolean =>
	structure.kind === 'direction_only' ? false : structure.intentToken.wasAliasMatched;

/**
 * The refs one parsed command resolved, in recency order: direct object first,
 * then the preposition-bound ref (plan §4 — what the next clause's "it" means).
 * Directions are not refs and never thread.
 *
 * @param command - The parsed command
 * @returns The resolved refs, most salient first (possibly empty)
 */
const resolvedRefsOf = (command: ParsedCommand): readonly EntityRef[] =>
	// D252: and the body an order was given to, after the things it was told to act on.
	[command.directObjectRef, command.indirectObjectRef, command.toolRef, command.addresseeRef].filter(
		(entityRef): entityRef is EntityRef => entityRef !== undefined,
	);

/**
 * Assemble an `unrecognized` outcome.
 *
 * @param originalInput - The raw input, exactly as typed
 * @param parsedPrefix - The cleanly-parsed clauses before the failure
 * @param diagnostics - Why parsing failed
 * @returns The outcome
 */
const unrecognized = (
	originalInput: string,
	parsedPrefix: readonly ParsedCommand[],
	diagnostics: ParseDiagnostics,
): ParseOutcome => ({ diagnostics, originalInput, parsedPrefix, type: 'unrecognized' });

